// The recommended WhatsApp template per order-notification event, per language. "Create
// template" next to an event opens the template manager prefilled with it (startFromPreset),
// the shop edits what it wants and submits it to Meta itself. Nothing is submitted
// automatically. Only German so far: a shop in another language gets the blank form.
//
// Every preset is a UTILITY template (SaveOrderNotificationRule accepts nothing else) and
// every body variable is a key of whatsappPlaceholders, so the editor turns it into a
// labelled pill with an example value; Meta rejects a variable without one. Meta also
// rejects a body that starts or ends with a variable.
import { ORDER_DETAILS_TOKEN, ORDER_TRACKING_TOKEN } from '@/extensions/communication/utils/whatsappPlaceholders';

const presetsByLanguage = {
    de: {
        orderDetailsButtonText: 'Bestellung ansehen',
        trackingButtonText: 'Sendung verfolgen',
        footer: 'Bei Fragen antworten Sie einfach auf diese Nachricht.',
        events: {
            'order.placed': {
                name: 'bestellbestaetigung_v1',
                body: 'Hallo {{1}}, vielen Dank für Ihre Bestellung {{2}} vom {{3}} über {{4}}. Wir melden uns hier, sobald es Neuigkeiten zu Ihrer Bestellung gibt.',
                variable_keys: ['recipient.name', 'order.number', 'order.date', 'order.amount'],
            },
            'order.status_changed': {
                name: 'bestellstatus_geaendert_v1',
                body: 'Hallo {{1}}, Ihre Bestellung {{2}} hat jetzt den Status {{3}}. Alle Details finden Sie in Ihrer Bestellübersicht.',
                variable_keys: ['recipient.name', 'order.number', 'order.status'],
            },
            'order.updated': {
                name: 'bestellung_aktualisiert_v1',
                body: 'Hallo {{1}}, Ihre Bestellung {{2}} wurde aktualisiert. Alle Details finden Sie in Ihrer Bestellübersicht.',
                variable_keys: ['recipient.name', 'order.number'],
            },
            'order.shipped': {
                name: 'bestellung_versendet_v1',
                body: 'Hallo {{1}}, Ihre Bestellung {{2}} ist unterwegs. Ihre Sendungsnummer lautet {{3}}, über die Schaltfläche können Sie die Sendung verfolgen.',
                variable_keys: ['recipient.name', 'order.number', 'order.tracking'],
                tracking: true,
            },
            'order.shipped.without_tracking': {
                name: 'bestellung_versendet_ohne_sendungsnummer_v1',
                body: 'Hallo {{1}}, Ihre Bestellung {{2}} wurde versendet und ist auf dem Weg zu Ihnen. Vielen Dank für Ihren Einkauf.',
                variable_keys: ['recipient.name', 'order.number'],
            },
            // No amount: order.amount is the order total, not what was refunded.
            'order.refunded': {
                name: 'bestellung_gutschrift_v1',
                body: 'Hallo {{1}}, zu Ihrer Bestellung {{2}} haben wir eine Gutschrift erstellt. Alle Details finden Sie in Ihrer Bestellübersicht.',
                variable_keys: ['recipient.name', 'order.number'],
            },
        },
    },
};

// language: the shop's default language code ("de", "de_DE"); regional variants share one set.
// withoutTracking: the shipped event's second template, for orders without a tracking number.
// withOrderLink: the storefront declares an order link (config.template.links "order"); without
// it the "View order" button has no URL to bake and Meta would reject the template.
export const orderNotificationTemplatePreset = (
    event,
    { language, withoutTracking = false, withOrderLink = false } = {}
) => {
    const presetLanguage = String(language || '')
        .slice(0, 2)
        .toLowerCase();
    const languageSet = presetsByLanguage[presetLanguage];
    const preset = languageSet && languageSet.events[withoutTracking ? `${event}.without_tracking` : event];
    if (!preset) {
        return null;
    }

    const { tracking, ...template } = preset;
    const buttons = [
        ...(tracking
            ? [{ type: 'URL', text: languageSet.trackingButtonText, url: ORDER_TRACKING_TOKEN, phone_number: '' }]
            : []),
        ...(withOrderLink
            ? [{ type: 'URL', text: languageSet.orderDetailsButtonText, url: ORDER_DETAILS_TOKEN, phone_number: '' }]
            : []),
    ];

    return { ...template, category: 'UTILITY', language: presetLanguage, footer: languageSet.footer, buttons };
};
